import { A, P, Small } from "@upsolve/ui";
import { format, isValid } from "date-fns";
import type { FluidObject } from "gatsby-image";
import { get } from "lodash";
import React, { Fragment } from "react";
import styled from "styled-components";
import * as DTI from "../../../utils/dataTestIds";
import ImageInContext from "../Media/ImageInContext";

type TImageFluid = FluidObject | FluidObject[] | null | undefined;

export type TAuthor = {
  name?: string | null;
  slug?: string | null;
  image?: {
    image?: {
      file?: {
        url?: string | null;
      } | null;
      fluid?: TImageFluid;
    } | null;
  } | null;
};

type TAuthorDisplay = {
  name?: string | null;
  slug?: string | null;
  photoUrl?: string | null;
  fluid?: TImageFluid;
};

export type TDateValue = string | number | Date | null | undefined;

export type TImageLoading = "auto" | "eager" | "lazy";

type TAuthorsBlockProps = {
  authors?: TAuthor[];
  createdAt?: TDateValue;
  reviewedAt?: TDateValue;
  updatedAt?: TDateValue;
  verb?: string;
  imageLoading?: TImageLoading;
};

const AuthorsBlock = ({
  authors = [],
  createdAt,
  reviewedAt,
  updatedAt,
  verb = "Written",
  imageLoading,
}: TAuthorsBlockProps): React.ReactElement => {
  const authorsFinal: TAuthorDisplay[] =
    authors.length > 0
      ? authors.map((author) => ({
          name: author.name,
          slug: author.slug,
          photoUrl: get(author, "image.image.file.url"),
          fluid: get(author, "image.image.fluid"),
        }))
      : [
          {
            name: "Upsolve Team",
            slug: "upsolve-team",
            photoUrl: "https://static.upsolve.org/logos/upsolve-icon-512.png",
          },
        ];

  const displayDate = reviewedAt ?? updatedAt ?? createdAt;

  return (
    <AuthorBlockWrapper data-test-id={DTI.AUTHORS_BLOCK}>
      <div className="content-author__images">
        {authorsFinal
          .filter((author) => author.photoUrl != null)
          .map((author, authorIndex) => (
            <ImageInContext
              key={authorIndex}
              className="author-image"
              alt={get(author, "name", "")}
              src={author.photoUrl}
              fluid={author.fluid}
              loading={imageLoading}
            />
          ))}
      </div>
      <P>
        <b>
          {verb} by{" "}
          {authorsFinal
            .filter(
              (author, authorsIndex, authorsArray) => authorsIndex === 0 || authorsIndex < authorsArray.length - 1
            )
            .map((author, authorIndex, authorsArray) => (
              <Fragment key={authorIndex}>
                <A aria-busy="false" href={`/authors/${author.slug}/`}>
                  {author.name}
                </A>
                {authorsArray.length > 1 && authorIndex !== authorsArray.length - 1 ? ", " : ""}
              </Fragment>
            ))}
          .&nbsp;
          {authorsFinal.length > 1 && (
            <Fragment>
              Legally reviewed by{" "}
              {authors.slice(-1).map((author, authorIndex) => (
                <A key={authorIndex} href={`/authors/${author.slug}/`}>
                  {author.name}
                </A>
              ))}
            </Fragment>
          )}
        </b>
        <br />
        {displayDate != null && isValid(new Date(displayDate)) && (
          <Small>
            {(() => {
              if (reviewedAt) return "Reviewed";
              if (updatedAt) return "Updated";
              return "Written";
            })()}{" "}
            {format(new Date(displayDate), "MMMM d, yyyy")}
          </Small>
        )}
      </P>
    </AuthorBlockWrapper>
  );
};

export const AuthorBlockWrapper = styled.div`
  display: flex;
  align-items: center;
  background: ${(props) => props.theme.colors.white["900"]};
  padding: 0.1em 0.5em;
  margin: -0.1em -0.5em;
  & > div {
    margin: 1.25em 0;
  }
  .author-image,
  img {
    height: 48px;
    width: 48px;
    min-height: 48px;
    min-width: 48px;
    border-radius: 36px;
  }
  & > p {
    margin: 1em 0.75em;
    font-size: 14px;
  }
  small {
    color: ${(props) => props.theme.colors.gray[500]};
  }
  &__images {
    display: flex;
    padding-right: 4px;
    & > * {
      margin: 0 -4px;
    }
  }
`;

export default AuthorsBlock;
